import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router';
import { AppShell } from './components/AppShell';
import { useSession } from './lib/session';
import { useTheme } from './lib/theme';
import { ComingSoonPage, NotFoundPage } from './pages/ComingSoon';
import { HomePage } from './pages/Home';
import { LoginPage } from './pages/Login';
import { MorePage } from './pages/More';
import { SetPasswordPage } from './pages/SetPassword';
import { UsersPage } from './pages/settings/Users';
import { SettingsPage } from './pages/settings/business/SettingsPage';
import { ChannelsPage } from './pages/settings/channels/ChannelsPage';
import { PrintersPage } from './pages/printers/PrintersPage';
import { FilamentsPage } from './pages/inventory/FilamentsPage';
import { SuppliesPage } from './pages/inventory/SuppliesPage';
import { CustomersPage } from './pages/customers/CustomersPage';
import { NewOrderPage } from './pages/orders/NewOrderPage';
import { OrderPage } from './pages/orders/OrderPage';
import { OrdersPage } from './pages/orders/OrdersPage';
import { QueuePage } from './pages/queue/QueuePage';
import { LabelsPage } from './pages/inventory/LabelsPage';
import { PurchasePage } from './pages/inventory/PurchasePage';
import { SpoolByCode, SpoolPage } from './pages/inventory/SpoolPage';
import { StockPhonePage } from './pages/inventory/StockPhonePage';
import { MediaLibraryPage } from './pages/media/MediaLibraryPage';
import { MediaUploadPage } from './pages/media/MediaUploadPage';
import { ProductPage } from './pages/products/ProductPage';
import { ProductsPage } from './pages/products/ProductsPage';
import { DataPage } from './pages/settings/data/DataPage';
import { QuickQuotePage } from './pages/quote/QuickQuotePage';
import { QuoteDetailPage } from './pages/quotes/QuoteDetailPage';
import { QuotesPage } from './pages/quotes/QuotesPage';
import { SetupWizard } from './pages/setup/SetupWizard';
import { ExpensesPage } from './pages/expenses/ExpensesPage';
import { NewExpenseRoute } from './pages/expenses/NewExpensePage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { ALL_ITEMS, NEW_MENU } from './nav';

export function Splash({ label = 'Carregando…' }: { label?: string }) {
  const { dz } = useTheme();
  return (
    <div className={dz}>
      <div className="splash" aria-busy="true">
        <div className="layers">
          <div className="fill" style={{ width: '60%' }} />
        </div>
        <p className="sm muted">{label}</p>
      </div>
    </div>
  );
}

/** Everything inside requires a session; otherwise go to /login?next=… */
function RequireSession() {
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <Splash />;
  if (session.isError) return <Splash label="Sem conexão com o servidor. Tentando de novo…" />;
  if (!session.data) {
    const next = location.pathname + location.search;
    return (
      <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />
    );
  }
  return <Outlet />;
}

const built = new Set([
  '/',
  '/configuracoes/usuarios',
  '/configuracoes/custos',
  '/configuracoes/canais',
  '/configuracoes/dados',
  '/produtos',
  '/midia',
  '/filamentos/compra',
  '/fila',
  '/fila?nova=1',
  '/pedidos',
  '/pedidos/novo',
  '/despesas',
  '/despesas/nova',
  '/relatorios',
]);
const comingSoon = [...ALL_ITEMS.map((i) => i.to), ...NEW_MENU.map((i) => i.to)]
  .filter((to, i, all) => !built.has(to) && all.indexOf(to) === i)
  .map((to) => ({ path: to, element: <ComingSoonPage /> }));

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/convite/:token', element: <SetPasswordPage kind="invite" /> },
  { path: '/redefinir-senha/:token', element: <SetPasswordPage kind="password_reset" /> },
  {
    element: <RequireSession />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomePage /> },
          { path: '/mais', element: <MorePage /> },
          { path: '/configuracoes', element: <Navigate to="/configuracoes/custos" replace /> },
          { path: '/configuracoes/usuarios', element: <UsersPage /> },
          { path: '/configuracoes/custos', element: <SettingsPage /> },
          { path: '/configuracoes/canais', element: <ChannelsPage /> },
          { path: '/configuracoes/dados/:tab?', element: <DataPage /> },
          { path: '/impressoras', element: <PrintersPage /> },
          { path: '/filamentos/compra', element: <PurchasePage /> },
          { path: '/filamentos/:tab?', element: <FilamentsPage /> },
          { path: '/carreteis/etiquetas', element: <LabelsPage /> },
          { path: '/carreteis/:id', element: <SpoolPage /> },
          { path: '/c/:code', element: <SpoolByCode /> },
          { path: '/estoque', element: <StockPhonePage /> },
          { path: '/fila', element: <QueuePage /> },
          { path: '/pedidos', element: <OrdersPage /> },
          { path: '/pedidos/novo', element: <NewOrderPage /> },
          { path: '/pedidos/:id', element: <OrderPage /> },
          { path: '/insumos', element: <SuppliesPage /> },
          { path: '/despesas/nova', element: <NewExpenseRoute /> },
          { path: '/despesas/:tab?', element: <ExpensesPage /> },
          { path: '/relatorios/:tab?', element: <ReportsPage /> },
          { path: '/orcamento-rapido', element: <QuickQuotePage /> },
          { path: '/orcamentos', element: <QuotesPage /> },
          { path: '/orcamentos/:id', element: <QuoteDetailPage /> },
          { path: '/clientes', element: <CustomersPage /> },
          { path: '/produtos', element: <ProductsPage /> },
          { path: '/produtos/:id/:tab?', element: <ProductPage /> },
          { path: '/midia', element: <MediaLibraryPage /> },
          { path: '/midia/enviar', element: <MediaUploadPage /> },
          { path: '/configuracao-inicial', element: <SetupWizard /> },
          ...comingSoon,
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
